// Pure stock take helpers: line status, variance flags, review totals and history
// formatting. Kept free of React so they can be unit tested.

// A variance is "big" when it is at least this many units, or this share of the
// expected quantity.
export const BIG_VARIANCE_UNITS = 10;
export const BIG_VARIANCE_RATIO = 0.5;

// Quantities that ask "are you sure?" before they are applied
export const IC_CONFIRM_MULTIPLIER = 2;   // above 2x the expected quantity
export const LARGE_QUANTITY = 500;        // free scan (no expected quantity)

// Above this share of unscanned IC lines, submitting needs the document number typed
export const UNSCANNED_CONFIRM_RATIO = 0.5;

export const lineStatus = (counted, expected) => {
  if (counted === 0) return 'not_scanned';
  if (counted < expected) return 'partial';
  if (counted === expected) return 'complete';
  return 'over';
};

export const isBigVariance = (diff, expected) => {
  if (!diff) return false;
  const size = Math.abs(diff);
  if (size >= BIG_VARIANCE_UNITS) return true;
  // Any stock on a line expected to be empty is a big surprise
  if (!expected) return true;
  return size / Math.abs(expected) >= BIG_VARIANCE_RATIO;
};

// IC document lines merged with the scanned counts (Map keyed by the document barcode)
export const buildIcLines = (icDocItems, scannedMap) =>
  icDocItems.map((item) => {
    const scanned = scannedMap.get(item.barcode);
    const scannedCount = scanned ? scanned.count : 0;
    const expectedCount = item.quantity;
    const diff = scannedCount - expectedCount;
    return {
      ...item,
      scannedCount,
      expectedCount,
      diff,
      status: lineStatus(scannedCount, expectedCount),
      bigVariance: isBigVariance(diff, expectedCount),
    };
  });

export const countStatuses = (lines) =>
  lines.reduce(
    (acc, line) => ({ ...acc, [line.status]: acc[line.status] + 1 }),
    { not_scanned: 0, partial: 0, over: 0, complete: 0 }
  );

// Totals for the review-before-submit dialog (IC mode)
export const summarizeIcReview = (lines) => {
  const counts = countStatuses(lines);
  const totalExpected = lines.reduce((sum, l) => sum + l.expectedCount, 0);
  const totalCounted = lines.reduce((sum, l) => sum + l.scannedCount, 0);
  const unscannedRatio = lines.length > 0 ? counts.not_scanned / lines.length : 0;
  return {
    lineCount: lines.length,
    matched: counts.complete,
    short: counts.partial,
    over: counts.over,
    uncounted: counts.not_scanned,
    totalExpected,
    totalCounted,
    netDiff: totalCounted - totalExpected,
    bigCount: lines.filter((l) => l.bigVariance).length,
    // Lines that differ from the document, biggest difference first
    variances: lines
      .filter((l) => l.status !== 'complete')
      .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff)),
    // Mostly (or entirely) unscanned: the operator must type the document number
    requiresDocConfirm: lines.length > 0 && (counts.not_scanned === lines.length || unscannedRatio > UNSCANNED_CONFIRM_RATIO),
  };
};

// Does setting a line to `quantity` need an extra confirmation?
export const quantityNeedsConfirm = (quantity, expected) => {
  if (!Number.isFinite(quantity)) return false;
  if (expected === undefined || expected === null) return quantity > LARGE_QUANTITY;
  return quantity > IC_CONFIRM_MULTIPLIER * Math.max(expected, 1) || quantity > LARGE_QUANTITY;
};

// Quantity field text -> whole number, or null while empty / invalid
export const parseQuantity = (text) => {
  const trimmed = String(text ?? '').trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = parseInt(trimmed, 10);
  return Number.isSafeInteger(value) ? value : null;
};

// Keep only digits (the field also receives pasted or scanned text)
export const sanitizeQuantityText = (text) => String(text ?? '').replace(/\D/g, '').slice(0, 6);

// Case-insensitive search over description, part name and barcode
export const filterLines = (lines, query) => {
  const q = String(query ?? '').trim().toLowerCase();
  if (!q) return lines;
  return lines.filter((l) =>
    [l.description, l.partName, l.barcode].some((v) => String(v ?? '').toLowerCase().includes(q))
  );
};

// After a successful submit, take the submitted counts off the free-scan map. Anything
// scanned while the submit was in flight is kept instead of being wiped.
export const subtractSubmitted = (map, submittedItems) => {
  const next = new Map(map);
  submittedItems.forEach(({ barcode, count }) => {
    const current = next.get(barcode);
    if (!current) return;
    if (current.count <= count) next.delete(barcode);
    else next.set(barcode, { ...current, count: current.count - count });
  });
  return next;
};

// "+3", "−2" (true minus sign), "0"
export const formatSigned = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return '–';
  if (n > 0) return `+${n}`;
  if (n < 0) return `−${Math.abs(n)}`;
  return '0';
};

export const formatUkDate = (value) => {
  if (!value) return 'N/A';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB');
};

export const formatUkTime = (value, { seconds = false } = {}) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' } : {}),
  });
};

// History documents carry `adjustments` (BARCODE, CURRENT, SCANNED, DIFF) and/or the
// Priority subform. The list and the detail view both count lines with this.
export const historyLines = (doc) => {
  if (!doc) return [];
  if (Array.isArray(doc.adjustments) && doc.adjustments.length > 0) return doc.adjustments;
  return Array.isArray(doc.TRANSORDER_C_SUBFORM) ? doc.TRANSORDER_C_SUBFORM : [];
};

// Use ?? (not ||) so a genuine DIFF of 0 isn't mistaken for "missing" and fall
// through to CQUANT, which would misclassify zero-adjustment rows.
export const historyDiff = (line) => line.DIFF ?? line.CQUANT ?? 0;
